import React, { useState, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Flex, Input, Select, Button, Heading, SimpleGrid, Text,
  Stack, Image, Badge, Container, Checkbox, CheckboxGroup,
} from '@chakra-ui/react'
import { Link } from 'react-router-dom'
import { GET_WORKSHEETS } from 'store/worksheets'

import eng from 'assets/img/5.png'
import mathPost from 'assets/img/1.png'
import mathPre from 'assets/img/2.png'
import sciPost from 'assets/img/3.png'
import sciPre from 'assets/img/4.png'
import social from 'assets/img/6.png'
import thai from 'assets/img/7.png'

const imageSubject = (subject, classNum) => {
  const num = classNum && classNum.split(' ')[1]
  if (subject === 'คณิตศาสตร์' && num < 4) return mathPre
  if (subject === 'คณิตศาสตร์' && num >= 4) return mathPost
  if (subject === 'วิทยาศาสตร์' && num < 4) return sciPre
  if (subject === 'วิทยาศาสตร์' && num >= 4) return sciPost
  if (subject === 'สังคมศึกษา') return social
  if (subject === 'ภาษาอังกฤษ') return eng
  if (subject === 'ภาษาไทย') return thai
}

const SearchBar = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState('')
  return (
    <Box mb={6}>
      <Input
        placeholder='ค้นหาใบงาน'
        size='lg'
        borderRadius='full'
        value={searchTerm}
        onChange={(e) => { setSearchTerm(e.target.value); onSearch(e.target.value) }}
      />
    </Box>
  )
}

const FilterBar = ({ onFilter }) => {
  const [worksheetType, setWorksheetType] = useState('all')
  const [classroom, setClassroom] = useState([])
  const [subject, setSubject] = useState([])

  const update = (newFilters) => onFilter(newFilters)

  return (
    <Stack spacing={4} p={4} borderWidth='1px' borderRadius='lg' shadow='sm' bg='white'>
      <Heading size='md' mb={2}>ตัวกรองใบงาน</Heading>
      <Box>
        <Flex gap={2}>
          <Button colorScheme={worksheetType === 'all' ? 'teal' : 'gray'}
            onClick={() => { setWorksheetType('all'); update({ worksheetType: 'all', classroom, subject }) }}>
            ใบงานทั้งหมด
          </Button>
          <Button colorScheme={worksheetType === 'free' ? 'teal' : 'gray'}
            onClick={() => { setWorksheetType('free'); update({ worksheetType: 'free', classroom, subject }) }}>
            ใบงานฟรี
          </Button>
        </Flex>
      </Box>
      <Flex direction={{ base: 'column', md: 'row', lg: 'column' }} p={1} gap={6}>
        <Box flex='1'>
          <Heading size='sm' mb={2}>เลือกชั้นเรียน</Heading>
          <CheckboxGroup value={classroom} onChange={(v) => { setClassroom(v); update({ worksheetType, classroom: v, subject }) }}>
            <Stack direction='column' spacing={2}>
              {['ประถมศึกษาปีที่ 1','ประถมศึกษาปีที่ 2','ประถมศึกษาปีที่ 3',
                'ประถมศึกษาปีที่ 4','ประถมศึกษาปีที่ 5','ประถมศึกษาปีที่ 6',
                'มัธยมศึกษาปีที่ 1','มัธยมศึกษาปีที่ 2','มัธยมศึกษาปีที่ 3'].map(c => (
                <Checkbox key={c} value={c}>{c}</Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
        </Box>
        <Box flex='1'>
          <Heading size='sm' mb={2}>เลือกวิชา</Heading>
          <CheckboxGroup value={subject} onChange={(v) => { setSubject(v); update({ worksheetType, classroom, subject: v }) }}>
            <Stack direction='column' spacing={2}>
              {['ภาษาไทย','คณิตศาสตร์','วิทยาศาสตร์','สังคมศึกษา','ภาษาอังกฤษ'].map(s => (
                <Checkbox key={s} value={s}>{s}</Checkbox>
              ))}
            </Stack>
          </CheckboxGroup>
        </Box>
      </Flex>
    </Stack>
  )
}

const WorksheetCard = ({ worksheet }) => {
  const discountedPrice = worksheet.sale_price
    ? (parseFloat(worksheet.original_price) - parseFloat(worksheet.sale_price)).toFixed(2)
    : null

  return (
    <Box bg='white' boxShadow='md' borderRadius='md' overflow='hidden'>
      <Image
        src={worksheet.url_image || imageSubject(worksheet.subject, worksheet.class)}
        alt={worksheet.name}
        w='full'
        objectFit='cover'
      />
      <Stack spacing={2} padding={4}>
        {worksheet.is_new && <Badge colorScheme='blue' w='fit-content'>ใหม่</Badge>}
        {worksheet.is_recommended && <Badge colorScheme='purple' w='fit-content'>แนะนำ</Badge>}
        {worksheet.is_bestseller && <Badge colorScheme='orange' w='fit-content'>ขายดี</Badge>}
        <Text fontWeight='bold' fontSize='lg' noOfLines={2}>{worksheet.name}</Text>
        <Text fontSize='sm' color='gray.600'>
          <Badge mr={2}>{worksheet.class}</Badge>
          <Badge>{worksheet.subject}</Badge>
        </Text>
        {worksheet.is_free ? (
          <Text fontWeight='bold' fontSize='xl' color='green.500'>ฟรี</Text>
        ) : (
          <Flex alignItems='center' gap={2}>
            {worksheet.sale_price ? (
              <>
                <Text fontWeight='bold' fontSize='xl' color='teal.600'>฿{worksheet.sale_price}</Text>
                <Text as='s' color='gray.500' fontSize='sm'>฿{worksheet.original_price}</Text>
              </>
            ) : (
              <Text fontWeight='bold' fontSize='xl' color='teal.600'>฿{worksheet.original_price}</Text>
            )}
          </Flex>
        )}
        {discountedPrice && <Badge colorScheme='orange'>ประหยัด ฿{discountedPrice}</Badge>}
        <Link to={`/worksheet/${worksheet.id}`}>
          <Button colorScheme='teal' mt={2} w='full'>ดูใบงานนี้</Button>
        </Link>
      </Stack>
    </Box>
  )
}

const WorksheetList = ({ worksheets, onSort }) => (
  <>
    <Flex justify='space-between' align='center' mb={4}>
      <Heading as='h2' size='lg' color='teal.600' />
      <Select placeholder='จัดเรียงตาม' onChange={(e) => onSort(e.target.value)} maxWidth='200px'>
        <option value='date'>วันที่ (ใหม่ที่สุด)</option>
        <option value='alphabet'>ตัวอักษร (A-Z)</option>
        <option value='price'>ราคา (ต่ำ-สูง)</option>
      </Select>
    </Flex>
    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
      {worksheets.map((w, i) => <WorksheetCard key={i} worksheet={w} />)}
    </SimpleGrid>
  </>
)

const WorksheetPage = () => {
  const dispatch = useDispatch()
  const initFetch = useCallback(() => dispatch(GET_WORKSHEETS('')), [dispatch])
  useEffect(() => { initFetch() }, [initFetch])

  const worksheets = useSelector((state) => state.worksheets.lists)
  const [filtered, setFiltered] = useState([])
  const [selectedType, setSelectedType] = useState('all')

  useEffect(() => {
    if (worksheets && worksheets.length > 0) setFiltered(worksheets.filter(w => w.status))
  }, [worksheets])

  const handleSearch = (term) => {
    setFiltered(worksheets.filter(w => w.status && w.name.toLowerCase().includes(term.toLowerCase())))
  }

  const handleFilter = ({ worksheetType, classroom, subject }) => {
    if (worksheetType) setSelectedType(worksheetType)
    let result = worksheets.filter(w => w.status)
    if (worksheetType === 'free') result = result.filter(w => w.is_free)
    if (classroom && classroom.length > 0) result = result.filter(w => classroom.includes(w.class))
    if (subject && subject.length > 0) result = result.filter(w => subject.includes(w.subject))
    setFiltered(result)
  }

  const handleSort = (opt) => {
    let sorted = [...filtered]
    if (opt === 'date') sorted.sort((a, b) => new Date(b.updated_timestamp) - new Date(a.updated_timestamp))
    else if (opt === 'alphabet') sorted.sort((a, b) => a.name.localeCompare(b.name))
    else if (opt === 'price') sorted.sort((a, b) => parseFloat(a.sale_price || a.original_price) - parseFloat(b.sale_price || b.original_price))
    setFiltered(sorted)
  }

  return (
    <Container maxW='7xl'>
      <Flex direction={{ base: 'column', lg: 'row' }} p={6} gap={6}>
        <Box flex='1' maxW={{ base: '100%', lg: '300px' }}>
          <FilterBar onFilter={handleFilter} />
        </Box>
        <Box flex='3'>
          <SearchBar onSearch={handleSearch} />
          <Heading as='h2' size='lg' color='teal.600' mb={4}>
            ใบงาน{selectedType === 'all' ? 'ทั้งหมด' : 'ฟรี'}
          </Heading>
          {filtered.length > 0 ? (
            <WorksheetList worksheets={filtered} onSort={handleSort} />
          ) : (
            <Text>ไม่พบใบงาน</Text>
          )}
        </Box>
      </Flex>
    </Container>
  )
}

export default WorksheetPage
